<template>
	<div class="modal fade" id="editSigningModal" tabindex="-1" role="dialog" aria-labelledby="editSigningModalLabel" aria-hidden="true" data-backdrop="static">
		<div class="modal-dialog modal-xl" role="document">
			<div class="modal-content">
				<div class="modal-header">
					<h5 class="modal-title" id="editSigningModalLabel">{{$t('signing.edit')}}</h5>
					<button type="button" class="close" data-dismiss="modal" aria-label="Close">
						<span aria-hidden="true">&times;</span>
					</button>
				</div>
				<div class="loader" v-if="isSaving"></div>
				<form @submit="onSubmit" v-show="!isSaving">
					<div class="modal-body">
						<div class="form-group">
							<div class="font-weight-bold required">{{$t('signing.headers.title')}}</div>
							<div class="w-100">
								<input class="form-control" v-model="model.title" v-validate="'required'" :name="'title'+_uid" :data-vv-as="$t('signing.headers.title')" />
								<span class="text-danger col-12" v-if="errors.has('title'+_uid)">{{ errors.first('title'+_uid) }}</span>
							</div>
						</div>
						<div class="form-group">
							<div class="font-weight-bold required">{{$t('request_n')}}</div>
							<div>
								<textarea class="form-control w-100" :name="'textarea'+_uid" :data-vv-as="$t('request_n')" v-model="model.request" v-validate="'required'"></textarea>
								<span class="text-danger col-12" v-if="errors.has('textarea'+_uid)">{{ errors.first('textarea'+_uid) }}</span>
							</div>
						</div>
						<div class="form-group">
							<div class="font-weight-bold required">{{$t('signing.headers.signatories')}}</div>
							<Signatories
                                v-model="model.signatories"
                                :emptyMesssage="$t('signing.noSignatoriesMsg')"
                                remove-add-btn>
                            </Signatories>
						</div>
						<div class="form-group">
							<div class="font-weight-bold">{{$t('signing.headers.deadline')}}</div>
							<DatePicker
								v-model="model.deadline"
								:min="model.deadline || new Date()"
								:data-vv-as="$t('signing.headers.deadline')"
								:error="errors.first($t('signing.headers.deadline'))"
								:fieldName="$t('signing.headers.deadline')"
							></DatePicker>
						</div>
						<div class="form-group">
							<div class="font-weight-bold">{{$t('signing.attachments')}}</div>
                            <div class="alert-warning">{{ $t('signing.allowedFilesTypesForSigning') }}</div>
                            <table class="table table-hover mt-2" v-if="model.attachments">
                                <tbody>
                                    <tr v-for="(item, index) in model.attachments" :key="index">
                                        <td class="py-0 align-middle">{{ item.description }}</td>
                                        <td class="py-0 align-middle">{{ item.fileName }}</td>
                                        <td class="text-right py-0">
                                            <file-crud :file="item"
                                                :showDescription="false"
                                                :editable="!item.id"
                                                :type="item.docTypeCode"
                                                hide-file-name
                                                :accept-filter="signingFilesAcceptFilter"
                                                @removeFile="onRemoveFile"
                                            >
                                            </file-crud>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                            <div v-if="!model.attachments.length || (model.attachments[model.attachments.length-1].content || model.attachments[model.attachments.length-1].id)">
                                <button class="btn btn-primary ml-0" @click="addAtachment">
                                    <i class="fa fa-plus"></i>
                                    {{$t('add')}}
                                </button>
                            </div>
						</div>
					</div>
					<div class="modal-footer">
						<button type="submit" class="btn btn-primary" :disabled="!model.signatories">{{$t('buttons.save.btnText')}}</button>
						<button type="button" class="btn btn-secondary" data-dismiss="modal">{{$t('buttons.cancel')}}</button>
					</div>
				</form>
			</div>
		</div>
	</div>
</template>

<script>
    import Signatories from '@/components/coordinations/coordinators-list';
	import DatePicker from '../datepicker/DateTimePicker';
	import FileCrud from '../common/FileCRUD';

	import { Signing, File } from '@/models/models';
	import signingService from '@/services/signing.service';
    import { mapGetters } from 'vuex';

export default {
	components: {
        Signatories,
		DatePicker,
        FileCrud
	},
	props: {
		show: {
			type: Boolean,
			default: false
		},
		value: Object
	},
	data() {
		return {
			model: new Signing(),
			isSaving: false
		};
	},
    computed: {
        ...mapGetters(['signingFilesAcceptFilter'])
    },
	methods: {
		onSubmit(e) {
			e.preventDefault();
			if (this.model.signatories.length) {
				this.$validator.validate().then(valid => {
					if (valid) {
						this.save();
					} else {
						console.log('Form not valid');
					}
				});
			}
		},
		save() {
			this.isSaving = true;
			let data = new FormData();
			data.append('id', this.model.id);
			data.append('deadline', new Date(this.model.deadline).toJSON());
			data.append('request', this.model.request);
			data.append('title', this.model.title);

			// signatories
			for (let index = 0; index < this.model.signatories.length; index++) {
				const element = this.model.signatories[index];
				if (element.id) {
					data.append(`signatories[${index}].id`, element.id);
				} else {
					data.append(`signatories[${index}].userId`, element.userId);
				}

				data.append(`signatories[${index}].sort`, element.sort || index);
			}

			// files
			if (this.model.attachments.length) {
				for (let index = 0; index < this.model.attachments.length; index++) {
					const attachment = this.model.attachments[index];
					if (attachment.id) {
						data.append(`attachments[${index}].id`, attachment.id);
						data.append(`attachments[${index}].fileName`, attachment.fileName);
					} else {
						data.append(`files`, attachment.content);
					}
				}
			}

			signingService
				.edit(data)
				.then(result => {
					this.$vs.notify({
						color: 'success',
						title: this.$t('updateSuccess'),
						text: ''
					});
					this.$emit('updated', this.model);
				})
				.catch(err => {
					this.$vs.notify({
						color: 'danger',
						title: this.$t('notifications.error'),
						text: this.$helpers.errorText(err)
					});
				})
				.then(() => {
					this.hide();
				});
		},
		onClose() {
			this.$emit('update:show', false);
			this.$emit('onClose');
			this.isSaving = false;
			this.model = new Signing();
			this.$validator.reset();
		},
		hide() {
			$(this.$el).modal('hide');
		},
		addAtachment() {
			this.model.attachments.push(new File());
		},
		onRemoveFile(_id) {
			this.model.attachments = this.model.attachments.filter(x => x._id !== _id);
		}
	},
	mounted() {
		// add modal close listener
		$(this.$el).on('hidden.bs.modal', this.onClose);
		$(this.$el).bootstrapMaterialDesign();
	},
	beforeDestroy() {
		// remove listener
		$(this.$el).off('hidden.bs.modal');
	},
	watch: {
		show: function(val) {
			if (val) {
				this.model = new Signing(this.value);

                signingService.getAttachments(this.model.id)
                    .then((result) => {
                        if (result.data && Array.isArray(result.data)) {
                            if (!this.model.attachments) this.model.attachments = [];

                            result.data.forEach(el => {
                                this.model.attachments.push(new File(el))
                            });
                        }
                    })
                    .catch((error) => {
                        console.log(error);
                    });

				$(this.$el).modal('show');
			} else {
				$(this.$el).modal('hide');
			}
		}
	}
};
</script>

<style lang="scss" scoped>
.radio-message {
	padding: 3px 3px 3px 5px;
	border-left: 3px solid #03a9f4;
	box-shadow: 1px 1px 1px 1px lightgrey;
}

div.radio label {
	color: inherit;
	cursor: pointer;
}

.bmd-form-group {
	padding-top: 1rem !important;
}
</style>
